/**
 * Autocomplete Dropdown
 * 
 * @Author: Focci
 * @Date: 2023-09-27 11:12:59
 * @Last Modified by: Focci
 * @Last Modified time: 2023-09-27 11:12:59
 */

'use client'

// import PropTypes from 'prop-types'
import useApp from '@hook/useApp'
import clsx from 'clsx'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import { houseSearchType } from '@lib/config'
import { useTranslations } from 'next-intl'
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react'
import { ButtonBase, Button } from '@comp/material'
import { uniqOptions } from '../lib'
// import ResposiveHeadbar from './ResposiveHeadbar'
import ResposiveTrigger from './ResposiveTrigger'
import ResposiveZones from './ResposiveZones'
import ResposivePanelWrapper from './ResposivePanelWrapper'
import ResposivePanel from './ResposivePanel'
import useSurroundingCheckbox from '../../useSurroundingCheckbox'
import SuburbSchoolPicker from './SuburbSchoolPicker'

function RouteBack({
  onClick = () => {}
}) {
  return (
    <div className="shrink-0 pl-3 pr-2 pt-2 mt-1">
      <ButtonBase onClick={onClick}>
        <div 
          className="
            w-4 h-4 rounded-sm bg-[#242831] text-white 
            flex items-center justify-center
          "
        >
          <ArrowBackIosNewIcon sx={{ fontSize: '.7rem' }} />
        </div>
      </ButtonBase>
    </div>
  )
}

function SubmitAction({
  showSubmitButton = false,
  handleSubmit = () => {},
  label = ''
}) {
  const { isMobile } = useApp()
  const tc = useTranslations('common')
  const ts = useTranslations('search')

  return (
    <div className="pr-3 pl-2 pt-2 shrink-0 mt-1">
      <Button
        disabled={!showSubmitButton}
        onClick={handleSubmit}
        sx={{
          '&.MuiButton-root': { 
            borderRadius: '9999px', 
            padding: '4px 12px',
            minWidth: 'auto' 
          } 
        }}
      >
        {label || (isMobile ? ts('search') : tc('complete'))}
      </Button>
    </div>
  )
}

function ResposiveCombine({
  showSurrounding = false, // 是否显示周边成交
  wrapperClassName = '',
  popoverConfirmButtonLabel = '',
  type,
  value = [],
  placeholder,
  onChange = () => {},
  onSearchButtonClick = () => {},
  Trigger = ResposiveTrigger,
  showSubburbButtonPicker = true,
  autocompleteProps = {},
  autocompleteTriggerProps = {},
  single = false,
  module,
}, ref) {
  const { isMobile } = useApp()
  const [anchorEl, setAnchorEl] = useState(null)
  const [changed, setChanged] = useState(false)

  const { surrounding, handleCheckboxChange, SurroundingCheckbox } = useSurroundingCheckbox()

  const open = Boolean(anchorEl)
  const handleOpen = useCallback((e) => setAnchorEl(e.currentTarget), [])
  const showSubmitButton = useMemo(
    () => (isMobile || changed) && value.length > 0, 
    [changed, isMobile, value.length]
  )

  const handleClose = useCallback(() => {
    if (anchorEl) { anchorEl.focus() }
    setAnchorEl(null)
  }, [anchorEl])

  const handleSurroundingCheckboxChange = useCallback((e) => {
    handleCheckboxChange(e)
    setChanged(true)
  }, [handleCheckboxChange])

  const handleChange = useCallback((event, val, reason, opt) => {
    setChanged(true)
    onChange(event, val, reason, opt)
  }, [onChange])

  // 提交
  const handleSubmit = useCallback(() => {
    setChanged(false)
    onSearchButtonClick(value, { surrounding })
    handleClose()
  }, [handleClose, onSearchButtonClick, surrounding, value])

  // 选择地区与学校
  const handleZoneSchoolChange = useCallback(({ data }) => {
    const tmp = uniqOptions([...value, ...data])
    handleChange(null, tmp, 'selectOption', { isZoneSchoolPicker: true })
  }, [handleChange, value])

  useImperativeHandle(ref, () => ({ setChanged, handleClose }))

  return (
    <div className={clsx(['max-w-full overflow-hidden', wrapperClassName])} id="resposive-autocomplete">
      <div className="flex relative">
        <Trigger
          onOpen={handleOpen}
          onSubmit={handleSubmit}
          value={value}
          placeholder={open ? '' : placeholder}
          showSubmitButton={showSubmitButton}
          {...autocompleteTriggerProps}
        />
        {showSubburbButtonPicker && value?.length === 0 && (
          <div className="absolute right-1 top-1/2 -translate-y-1/2">
            <SuburbSchoolPicker onChange={handleZoneSchoolChange} />
          </div>
        )}
      </div>
      <ResposivePanelWrapper
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <ResposivePanel 
          open={open}
          changed={!isMobile && changed}
          className={clsx([isMobile ? 'max-h-[100vh]' : 'max-h-[60vh]'])}
          type={type}
          value={value}
          autocompleteProps={autocompleteProps}
          onSubmit={handleSubmit}
          onChange={handleChange}
          inputUndersideChild={
            // https://7thave.atlassian.net/browse/HT-4141?focusedCommentId=17112
            type !== houseSearchType.estimate && (
              <>
                {showSurrounding && (
                  <div className="px-2 pb-2">
                    <SurroundingCheckbox
                      checked={surrounding}
                      onChange={handleSurroundingCheckboxChange}
                    />
                  </div>
                )}
                <ResposiveZones 
                  single={single} 
                  module={module}
                  onSubmit={handleZoneSchoolChange}
                />
              </>
            )
          }
          back={isMobile && <RouteBack onClick={handleClose} />}
          action={
            isMobile && <SubmitAction 
              label={popoverConfirmButtonLabel}
              showSubmitButton={showSubmitButton}
              handleSubmit={handleSubmit}
            />
          }
        />
      </ResposivePanelWrapper>
    </div>
  )
}

export default forwardRef(ResposiveCombine)

// ResposiveCombine.propTypes = {
//   wrapperClassName: PropTypes.string,
//   type: PropTypes.string,
//   value: PropTypes.array,
//   placeholder: PropTypes.string,
//   onChange: PropTypes.func,
//   onSearchButtonClick: PropTypes.func,
//   Trigger: PropTypes.node,
//   autocompleteProps: PropTypes.object,
//   autocompleteTriggerProps: PropTypes.object,
//   single: PropTypes.bool,
//   module: PropTypes.string,
// }
